/**
 * Status Tag Component
 * Auto-colored status badges using custom design tokens
 * Replaces scattered status tag implementations
 */

import React from 'react';
import { Tag } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { colors } from '../../theme/designTokens';

/**
 * Status Tag with auto-coloring
 *
 * @param {string} status - Status value (pass, fail, warning, etc.)
 * @param {boolean} showIcon - Show status icon (default: false)
 * @param {boolean} uppercase - Uppercase text (default: true)
 * @param {object} style - Additional styles
 */
function StatusTag({ status, showIcon = false, uppercase = true, style = {}, ...props }) {
  if (!status) return null;

  const statusLower = String(status).toLowerCase();
  const text = uppercase ? String(status).toUpperCase() : String(status);

  // Get custom colors from design tokens
  const getStatusStyle = () => {
    if (['pass', 'passed', 'success', 'active', 'enabled', 'completed', 'online'].includes(statusLower)) {
      return {
        color: colors.status.pass,
        backgroundColor: colors.status.passBg,
        borderColor: colors.status.passLight,
      };
    }
    if (['fail', 'failed', 'error', 'inactive', 'disabled', 'offline'].includes(statusLower)) {
      return {
        color: colors.status.fail,
        backgroundColor: colors.status.failBg,
        borderColor: colors.status.fail,
      };
    }
    if (['warning', 'pending'].includes(statusLower)) {
      return {
        color: colors.status.warning,
        backgroundColor: colors.status.warningBg,
        borderColor: colors.status.warning,
      };
    }
    if (['in_progress', 'running', 'syncing'].includes(statusLower)) {
      return {
        color: colors.status.running,
        backgroundColor: colors.status.runningBg,
        borderColor: colors.status.running,
      };
    }
    if (['unknown', 'not_evaluated'].includes(statusLower)) {
      return {
        color: colors.status.unknown,
        backgroundColor: colors.status.unknownBg,
        borderColor: colors.status.unknown,
      };
    }
    // Default
    return {
      color: colors.text.secondary,
      backgroundColor: colors.neutral[100],
      borderColor: colors.neutral[300],
    };
  };

  const statusStyle = getStatusStyle();

  // Map icons to status
  const getIcon = () => {
    if (!showIcon) return null;

    const statusLower = String(status).toLowerCase();

    if (['pass', 'passed', 'success', 'active', 'enabled', 'completed', 'online'].includes(statusLower)) {
      return <CheckCircleOutlined />;
    }
    if (['fail', 'failed', 'error', 'inactive', 'disabled', 'offline'].includes(statusLower)) {
      return <CloseCircleOutlined />;
    }
    if (['warning', 'pending'].includes(statusLower)) {
      return <ExclamationCircleOutlined />;
    }
    if (['in_progress', 'running', 'syncing'].includes(statusLower)) {
      return <SyncOutlined spin />;
    }
    if (['unknown', 'not_evaluated'].includes(statusLower)) {
      return <QuestionCircleOutlined />;
    }

    return null;
  };

  return (
    <Tag
      icon={getIcon()}
      style={{
        ...statusStyle,
        ...style
      }}
      {...props}
    >
      {text}
    </Tag>
  );
}

export default StatusTag;
